import { Card, CardContent, CardHeader, CardTitle } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";
import type {
  LifecycleAccountRef,
  LifecycleFundedRef,
  LifecycleOutcomes,
} from "../../metrics/computeLifecycle";
import type { ReportMetrics } from "../../metrics/types";
import { withCount } from "./formatValue";

/** Libellé d'un compte : « Nom · PropFirm » quand la prop firm est connue. */
function accountLabel(a: LifecycleAccountRef): string {
  return a.propFirmName ? `${a.name} · ${a.propFirmName}` : a.name;
}

/** Ordre d'affichage du devenir des comptes ajoutés. */
const OUTCOME_ORDER: Array<keyof LifecycleOutcomes> = ["blown", "likelyBlown", "passed", "active"];

function Counter({
  label,
  count,
  accounts,
  toneClass,
  note,
  unlinkedLabel,
}: {
  label: string;
  count: number;
  accounts: Array<LifecycleAccountRef | LifecycleFundedRef>;
  toneClass: string;
  note?: string;
  unlinkedLabel?: string;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-xl font-mono font-semibold ${toneClass}`}>{count}</div>
      {accounts.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {accounts.map((a, i) => (
            <li key={`${a.name}-${i}`} className="text-xs text-muted-foreground truncate">
              {accountLabel(a)}
              {unlinkedLabel && "unlinked" in a && a.unlinked && (
                <span className="opacity-70"> ({unlinkedLabel})</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {note && <div className="text-xs text-muted-foreground mt-1">{note}</div>}
    </div>
  );
}

/** Mouvements de comptes sur la période : ajoutés, perdus, passés funded.
 *  Retourne null si le snapshot est en v1 (lifecycle absent/null). */
export function LifecycleSection({ metrics }: { metrics: ReportMetrics }) {
  const t = useTranslation();
  const lifecycle = metrics.lifecycle;
  if (!lifecycle) return null;

  const outcomes = lifecycle.outcomes;
  const outcomeLabels: Record<keyof LifecycleOutcomes, string> = {
    blown: t.reports.lifecycleOutcomeBlown,
    likelyBlown: t.reports.lifecycleOutcomeLikelyBlown,
    passed: t.reports.lifecycleOutcomePassed,
    active: t.reports.lifecycleOutcomeActive,
  };

  // Les probablement cramés comptent comme un mouvement : sans eux dans ce
  // test, une période où seuls des comptes ont été fermés sans motif afficherait
  // « aucun mouvement » tout en masquant la mention qui dit le contraire.
  const hasEvents =
    lifecycle.added.count > 0 ||
    lifecycle.blown.count > 0 ||
    lifecycle.blown.lowConfidenceCount > 0 ||
    lifecycle.funded.count > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t.reports.lifecycleTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        {hasEvents ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Counter
              label={t.reports.lifecycleAdded}
              count={lifecycle.added.count}
              accounts={lifecycle.added.accounts}
              toneClass="text-foreground"
            />
            <Counter
              label={t.reports.lifecycleBlown}
              count={lifecycle.blown.count}
              accounts={lifecycle.blown.accounts}
              toneClass="text-red-500"
              note={
                lifecycle.blown.lowConfidenceCount > 0
                  ? withCount(
                      t.reports.lifecycleLowConfidence,
                      lifecycle.blown.lowConfidenceCount,
                    )
                  : undefined
              }
            />
            <Counter
              label={t.reports.lifecycleFunded}
              count={lifecycle.funded.count}
              accounts={lifecycle.funded.accounts}
              toneClass="text-emerald-500"
              unlinkedLabel={t.reports.lifecycleUnlinked}
            />
            {outcomes && lifecycle.added.count > 0 && (
              <div className="sm:col-span-3 border-t border-border pt-3">
                <div className="text-xs text-muted-foreground">
                  {withCount(t.reports.lifecycleOutcomesTitle, lifecycle.added.count)}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  {OUTCOME_ORDER.filter((k) => outcomes[k] > 0).map((k) => (
                    <span key={k} className="text-muted-foreground">
                      {outcomeLabels[k]}{" "}
                      <span className="font-mono font-semibold text-foreground">{outcomes[k]}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">{t.reports.lifecycleNoEvents}</div>
        )}
      </CardContent>
    </Card>
  );
}
